import type { BaseClient } from '../../http/BaseClient.js';
import { unwrapData } from '../../common/envelope.js';
import type { Organization } from '../organization/types.js';
import type {
  AppendProgramConfigRequest,
  AssociatedOrg,
  BillingProgramConfig,
  GeneratePayoutsRequest,
  ResellerCommission,
  ResellerPayout,
  ResellerPerformance,
  SetResellerRequest,
  UpdatePayoutRequest,
} from './types.js';

/**
 * Reseller surface. The first three methods are self-service for an org with
 * `isReseller === true` (the server gates on it). The remaining methods are
 * platform-admin only (gated on the caller's home org being the admin org).
 */
export class ResellerClient {
  constructor(private readonly http: BaseClient) {}

  // --- Reseller self-service (org.isReseller) ---

  /** Orgs associated to the caller reseller, with gross + commission rollups. */
  async listAssociatedOrgs(): Promise<AssociatedOrg[]> {
    const res = await this.http.get<{ success: boolean; data: AssociatedOrg[] }>('/reseller/associated-orgs');
    return res.data ?? [];
  }

  /** The caller reseller's commission ledger. */
  async listCommissions(): Promise<ResellerCommission[]> {
    const res = await this.http.get<{ success: boolean; data: ResellerCommission[] }>('/reseller/commissions');
    return res.data ?? [];
  }

  /** The caller reseller's payout history. */
  async listPayouts(): Promise<ResellerPayout[]> {
    const res = await this.http.get<{ success: boolean; data: ResellerPayout[] }>('/reseller/payouts');
    return res.data ?? [];
  }

  // --- Platform-admin only ---

  /** List all reseller orgs with aggregate performance. */
  async listResellers(): Promise<ResellerPerformance[]> {
    const res = await this.http.get<{ success: boolean; data: ResellerPerformance[] }>('/admin/resellers');
    return res.data ?? [];
  }

  /** Designate an org as a reseller (optionally with a per-reseller bps override). */
  async setReseller(orgId: string, body: SetResellerRequest = {}): Promise<Organization> {
    const res = await this.http.post<{ success: boolean; data: Organization }>(
      `/admin/orgs/${orgId}/reseller`,
      { body },
    );
    return unwrapData(res);
  }

  /** Revoke an org's reseller status. */
  async revokeReseller(orgId: string): Promise<Organization> {
    const res = await this.http.delete<{ success: boolean; data: Organization }>(`/admin/orgs/${orgId}/reseller`);
    return unwrapData(res);
  }

  /** End an org's reseller association (stamps resellerDetachedAt). */
  async detachAssociation(orgId: string): Promise<Organization> {
    const res = await this.http.post<{ success: boolean; data: Organization }>(
      `/admin/orgs/${orgId}/detach-reseller`,
      { body: {} },
    );
    return unwrapData(res);
  }

  /** The billing-program-config journal, newest first. */
  async getProgramConfig(): Promise<BillingProgramConfig[]> {
    const res = await this.http.get<{ success: boolean; data: BillingProgramConfig[] }>(
      '/admin/billing-program-config',
    );
    return res.data ?? [];
  }

  /** Append a new program-config period (closes the prior open row). */
  async appendProgramConfig(body: AppendProgramConfigRequest): Promise<BillingProgramConfig> {
    const res = await this.http.post<{ success: boolean; data: BillingProgramConfig }>(
      '/admin/billing-program-config',
      { body },
    );
    return unwrapData(res);
  }

  /** Draft payouts from accrued commissions in the given period. */
  async generatePayouts(body: GeneratePayoutsRequest): Promise<ResellerPayout[]> {
    const res = await this.http.post<{ success: boolean; data: ResellerPayout[] }>(
      '/admin/reseller-payouts/generate',
      { body },
    );
    return res.data ?? [];
  }

  /** Approve or mark-paid a payout (and optionally record an external ref). */
  async updatePayout(payoutId: string, body: UpdatePayoutRequest): Promise<ResellerPayout> {
    const res = await this.http.patch<{ success: boolean; data: ResellerPayout }>(
      `/admin/reseller-payouts/${payoutId}`,
      { body },
    );
    return unwrapData(res);
  }
}
